import React from "react";

interface PartnerOfferCardProps {
  /** Content for the coloured band at the top (logo image or icon) */
  header: React.ReactNode;
  /** Tailwind background class for the band (default: Emma/Afforda dark) */
  bandClassName?: string;
  /** Description shown under the band */
  children: React.ReactNode;
  /** Renders the card as a link when provided */
  href?: string;
  /** Opens the link in a new tab (default: true when href is set) */
  openInNewTab?: boolean;
  /** Click handler, used when the card is not a link */
  onClick?: () => void;
  /** Value for the data-track analytics attribute */
  dataTrack?: string;
  /** Highlights the card (e.g. when deep-linked to it) */
  isHighlighted?: boolean;
  /** Extra classes for the outer card */
  className?: string;
  /** Ref to the outer card element */
  innerRef?: React.Ref<HTMLDivElement>;
  /** Accessible label when the description alone is not descriptive enough */
  ariaLabel?: string;
}

export const OFFER_BAND_DARK = "bg-[#17102A]";
export const OFFER_BAND_LOQBOX = "bg-[#4E358C]";

/**
 * Partner / resource card used in the Offers carousel on the customer home
 * page and the "What are my options?" cards on the regret page: a coloured
 * band holding a logo or icon, with a short description underneath.
 */
const PartnerOfferCard: React.FC<PartnerOfferCardProps> = ({
  header,
  bandClassName = OFFER_BAND_DARK,
  children,
  href,
  openInNewTab = true,
  onClick,
  dataTrack,
  isHighlighted = false,
  className = "",
  innerRef,
  ariaLabel,
}) => {
  const cardClasses = `bg-white w-44 sm:w-48 flex-shrink-0 flex flex-col overflow-hidden text-left rounded-20 transition duration-300 ${
    isHighlighted ? "border-4 border-primary" : "hover:shadow-md"
  } ${className}`;

  const content = (
    <>
      <div
        data-testid="partner-offer-band"
        className={`w-full h-[85px] flex items-center justify-center overflow-hidden ${bandClassName}`}
      >
        {header}
      </div>
      <p className="whitespace-normal text-sm font-semibold leading-[135%] text-black px-[15px] pt-2.5 pb-[15px]">
        {children}
      </p>
    </>
  );

  return (
    <div
      ref={innerRef}
      data-track={dataTrack}
      data-testid="partner-offer-card"
      className={cardClasses}
    >
      {href ? (
        <a
          href={href}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          className="flex flex-col h-full"
          aria-label={ariaLabel}
          onClick={onClick}
        >
          {content}
        </a>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="flex flex-col h-full w-full"
          aria-label={ariaLabel}
        >
          {content}
        </button>
      )}
    </div>
  );
};

export default PartnerOfferCard;
